import ChevronDownIcon from '@src/assets/icons/chevron-down.svg';
import { Themed } from '@theme-ui/mdx';
import { addMonths } from 'date-fns';
import isFuture from 'date-fns/isFuture';
import isToday from 'date-fns/isToday';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import { Box, Button, Flex, Grid } from 'theme-ui';
import CalendarFooter from './calendarFooter';
import SelectedDates from './selectedDates';

function getDayOfCurrentWeek(index) {
  const today = new Date();
  const first = today.getDate() - today.getDay() + index;
  return new Date(today.setDate(first));
}

const CalendarSlotTypes = {
  1: 'Available',
  2: 'Booked',
  3: 'Disabled',
  Available: 1,
  Booked: 2,
  Disabled: 3,
};

const weekDayFromApi = (n) => {
  if (n === 6) {
    return 0;
  }

  return n + 1;
};
const emptyTimeSlots = Array.from({ length: 13 }, (v, i) => ({
  hour: 7 + i,
  slotType: CalendarSlotTypes.Disabled,
}));
const emptyCalendar = Array.from({ length: 7 }, (v, i) => ({
  date: getDayOfCurrentWeek(i + 1),
  weekDay: weekDayFromApi(i),
  timeSlots: emptyTimeSlots,
}));

const CalendarLoader = () => (
  <Box
    bg="primaryPaleBg"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.5,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Loader active />
  </Box>
);

const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const ltMonthsMap = [
  'Sausio',
  'Vasario',
  'Kovo',
  'Balandžio',
  'Gegužės',
  'Birželio',
  'Liepos',
  'Rugpjūčio',
  'Rugsėjo',
  'Spalio',
  'Lapkričio',
  'Gruodžio',
];

const generateCalendarHeader = (dates, language) => {
  const startDate = new Date(dates[0].date);
  const endDate = new Date(dates[6].date);

  let monthStart = startDate.toLocaleString(language, {
    month: 'long',
  });
  let monthEnd = endDate.toLocaleString(language, {
    month: 'long',
  });
  const dayStart = startDate.toLocaleString(language, {
    day: 'numeric',
  });
  const dayEnd = endDate.toLocaleString(language, {
    day: 'numeric',
  });

  if (language === 'lt') {
    monthStart = ltMonthsMap[startDate.getMonth()];
    monthEnd = ltMonthsMap[endDate.getMonth()];
  }

  if (monthStart === monthEnd) {
    return `${capitalizeFirstLetter(monthStart)} ${dayStart} - ${dayEnd}`;
  } else {
    return `${capitalizeFirstLetter(
      monthStart
    )} ${dayStart} - ${capitalizeFirstLetter(monthEnd)} ${dayEnd}`;
  }
};

const HeaderButton = ({ isPrev, isNext, onClick, isLoading }) => (
  <Button
    variant="empty"
    className="calendar-selector__button"
    onClick={onClick}
    disabled={isLoading}
  >
    <ChevronDownIcon
      sx={{
        right: isPrev && '2px',
        left: isNext && '2px',
        transform: isPrev ? 'rotate(90deg)' : 'rotate(-90deg)',
      }}
    />
  </Button>
);

const CalendarLegendItem = ({ bgColor, text }) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: '6px',
        lineHeight: '1.2',
        fontSize: '14px',
      }}
    >
      <Box
        sx={{
          width: '20px',
          padding: '15px 2px',
          border: '1px solid #D4E1FF',
          borderRadius: '5px',
          marginBottom: '4px',
          textAlign: 'center',
          background: bgColor,
        }}
      />{' '}
      {text}
    </Flex>
  );
};

const calendarSelector = ({
  isLoading,
  calendarData,
  onPrev,
  onNext,
  reset,
  onSelect,
  showCalendar,
  lessonCount,
  showSelectedDates,
  setShowSelectedDates,
  selectedTimes,
  setSelectedTimes,
  price,
  currency,
  selectedDiscount,
  priceWithDiscount,
  discountOptions,
  teacherSubjects,
}) => {
  const { t, i18n } = useTranslation();
  const [lessonsDatesLeftToChoose, setLessonsDatesLeftToChoose] =
    useState(lessonCount);

  useEffect(() => {
    if (reset) {
      reset();
    }
  }, []);
  const vm = {
    isLoading: isLoading,
    isEmpty: !calendarData,
    selectedWeekTitle:
      calendarData && generateCalendarHeader(calendarData, i18n.language),
    selectedWeekDay: calendarData && calendarData[0].date,
    calendarData: calendarData || emptyCalendar,
  };
  useEffect(() => {
    setLessonsDatesLeftToChoose(lessonCount - selectedTimes?.length);
  }, [lessonCount, selectedTimes]);

  const bgAvailableSlot = '#DDFFE7';
  const bgBookedSlot = '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      {showSelectedDates ? (
        <SelectedDates
          selectedTimes={selectedTimes}
          setSelectedTimes={setSelectedTimes}
          lessonCount={lessonCount}
          price={price}
          currency={currency}
          selectedDiscount={selectedDiscount}
          priceWithDiscount={priceWithDiscount}
          discountOptions={discountOptions}
          setShowSelectedDates={setShowSelectedDates}
        />
      ) : (
        <Box
          className="calendar-selector"
          sx={{
            position: 'relative',
          }}
        >
          {vm.isLoading && <CalendarLoader />}

          <Flex
            pb={4}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <HeaderButton onClick={onPrev} isLoading={isLoading} isPrev />
            <Themed.h5 sx={{ margin: 0 }}>{vm.selectedWeekTitle}</Themed.h5>
            <HeaderButton onClick={onNext} isLoading={isLoading} isNext />
          </Flex>

          <Box
            sx={{
              overflow: 'auto',
              opacity: showCalendar ? '1' : '0.3',
              pointerEvents: showCalendar ? 'auto' : 'none',
            }}
          >
            <Box>
              <Grid columns={7} gap={['2px', '12px']}>
                {vm.calendarData.map(({ weekDay, timeSlots, date }) => {
                  const isWeekend = weekDay === 6 || weekDay === 0;
                  const weekDayName = new Date(date).toLocaleString(
                    i18n.language,
                    {
                      weekday: 'short',
                    }
                  );
                  const weekDayNumber = new Date(date).toLocaleString(
                    i18n.language,
                    {
                      day: '2-digit',
                    }
                  );

                  return (
                    <Box key={date}>
                      <Box
                        pb={3}
                        sx={{
                          color: isWeekend ? 'primary' : 'inherit',
                          textTransform: 'capitalize',
                          textAlign: 'center',
                        }}
                      >
                        <span sx={{ fontSize: '16px' }}>{weekDayNumber}</span>
                        <br />
                        {weekDayName}
                      </Box>

                      {timeSlots.map(
                        ({
                          hour,
                          slotType,
                          lessonType,
                          lessonStudentCount,
                          lessonMax,
                        }) => {
                          const isAvailable =
                            slotType === CalendarSlotTypes.Available;
                          const isBooked =
                            slotType === CalendarSlotTypes.Booked;
                          const isSelected = selectedTimes?.find(
                            (selectedTime) => {
                              return (
                                selectedTime.date === date &&
                                selectedTime.hour === hour
                              );
                            }
                          );

                          const currentDate = new Date();
                          const cellDate = new Date(date);
                          const timeFromNow = (numberOfMonthsAhead) => {
                            const startDate = selectedTimes?.[0]?.date
                              ? new Date(selectedTimes?.[0]?.date)
                              : currentDate;
                            return addMonths(startDate, numberOfMonthsAhead);
                          };

                          // todo: disable 15min before class
                          // Added +1 to hour to disable slots one hour in advance reference task #7 in github
                          const isDayInFuture =
                            (lessonCount <= 8
                              ? (cellDate < timeFromNow(2) &&
                                  isFuture(new Date(cellDate))) ||
                                (isToday(new Date(cellDate)) &&
                                  currentDate.getHours() + 1 < hour)
                              : cellDate < timeFromNow(3) &&
                                isFuture(new Date(cellDate))) ||
                            (isToday(new Date(cellDate)) &&
                              currentDate.getHours() + 1 < hour);

                          let backgroundColor = '';
                          if (isAvailable && isDayInFuture)
                            backgroundColor = bgAvailableSlot;
                          if (isSelected) backgroundColor = 'primary';

                          return (
                            <Box
                              key={hour}
                              sx={{
                                width: ['36px', '55px'],
                                padding: [0, '2px'],
                                border: '1px solid #D4E1FF',
                                borderRadius: '5px',
                                marginBottom: '4px',
                                textAlign: 'center',
                                fontSize: ['12px', '16px'],
                                background: backgroundColor,
                                color: isSelected ? 'white' : '',
                                cursor:
                                  isAvailable && isDayInFuture
                                    ? 'pointer'
                                    : 'default',
                                '&:before': {
                                  content: '""',
                                  lineHeight: '1em',
                                  display: 'inline-block',
                                },
                              }}
                              onClick={() => {
                                if (isAvailable && isDayInFuture) {
                                  if (lessonCount === 1) {
                                    setSelectedTimes([
                                      {
                                        date,
                                        hour,
                                        lessonType,
                                        lessonStudentCount,
                                        lessonMax,
                                      },
                                    ]);
                                  } else {
                                    if (isSelected) {
                                      setSelectedTimes(
                                        selectedTimes?.filter(
                                          (selectedTime) =>
                                            selectedTime.date !== date ||
                                            selectedTime.hour !== hour
                                        )
                                      );
                                    } else if (lessonsDatesLeftToChoose > 0) {
                                      const newTime = {
                                        date,
                                        hour,
                                        lessonType,
                                        lessonStudentCount,
                                        lessonMax,
                                      };
                                      const updatedTimes = [
                                        ...selectedTimes,
                                        newTime,
                                      ];
                                      const sortedTimes = updatedTimes.sort(
                                        (a, b) => {
                                          const dateComparison =
                                            new Date(a.date) - new Date(b.date);
                                          if (dateComparison !== 0) {
                                            // If dates are different, sort by date
                                            return dateComparison;
                                          } else {
                                            // If dates are the same, sort by hour
                                            return a.hour - b.hour;
                                          }
                                        }
                                      );

                                      setSelectedTimes(sortedTimes);
                                    }
                                  }

                                  onSelect({
                                    day: date,
                                    time: hour,
                                    lessonType,
                                    lessonStudentCount,
                                    lessonMax,
                                  });
                                }
                              }}
                            >
                              {(isAvailable || isBooked) && <>{hour}.00 </>}
                            </Box>
                          );
                        }
                      )}
                    </Box>
                  );
                })}
              </Grid>

              <Flex
                sx={{
                  gap: '20px',
                  marginTop: '12px',
                  justifyContent: 'center',
                }}
              >
                <CalendarLegendItem
                  text={t('calendarSlot.booked')}
                  bgColor={bgBookedSlot}
                />
                <CalendarLegendItem
                  text={t('calendarSlot.available')}
                  bgColor={bgAvailableSlot}
                />
              </Flex>
            </Box>
          </Box>
        </Box>
      )}
      <CalendarFooter
        showCalendar={showCalendar}
        lessonCount={lessonCount}
        lessonsDatesLeftToChoose={lessonsDatesLeftToChoose}
        showSelectedDates={showSelectedDates}
        teacherSubjects={teacherSubjects}
      />
    </Box>
  );
};

export default calendarSelector;
